import { useQuery } from "@apollo/client/react";
import {
  DSCardWrapper,
  DSLinkButton,
} from "@components/design-system/molecules";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  faArrowRight,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import NewPatient from "./NewPatient";
import { PatientSearchBar } from "./PatientSearchBar";
import {
  GET_PATIENT_ANALYTICS,
  PatientFilteringOptions,
} from "../analytics/PatientAnalytics";
import PageTitle from "../layout/PageTitle";

function Patients({
  isForPatientSelectionAction = false,
  setSelectedPatientName,
  setSelectedPatientUuid,
}) {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("none");
  const [ascending, setAscending] = useState(true);
  const [shouldShowFilteringOptions, setShouldShowFilteringOptions] =
    useState(false);

  let location = useLocation();
  const [shouldShow, setShouldShow] = useState(
    location?.state?.shouldShow,
  );

  const { refetch, data, loading } = useQuery(GET_PATIENT_ANALYTICS, {
    variables: {
      sortBy,
      filterBy,
      ascending,
    },
    fetchPolicy: "network-only",
  });

  const filteredItems = useMemo(
    () => (data && !loading ? data?.patientAnalytics : []),
    [data, loading],
  );

  const loadingTrigger = loading || data === undefined;

  function onPatientClick(uuid) {
    if (!isForPatientSelectionAction) {
      navigate(`/notebook/${uuid}`);
    }
  }

  if (!loading && !data) {
    return null;
  } else {
    return (
      <div>
        {isForPatientSelectionAction ? null : (
          <>
            <PageTitle title="Patient Notebook" />
            <DSCardWrapper variant="info">
              <p>
                <FontAwesomeIcon icon={faInfoCircle} />{" "}
                <strong>Did You Know?</strong> You don't have to
                create patients to use Scribenote! Just start
                recording and your detected patient's name will be
                used in your note's title.{" "}
                <DSLinkButton href="/dashboard">
                  Try it out! <FontAwesomeIcon icon={faArrowRight} />
                </DSLinkButton>
              </p>
            </DSCardWrapper>
          </>
        )}
        <div className="flex w-full pt-5 flex-row">
          <div
            id="patientSearch"
            className="flex-1 w-full mr-2 md:mr-10"
          >
            <div className="w-full flex flex-col">
              <button
                className="self-end mb-2"
                onClick={() =>
                  setShouldShowFilteringOptions(
                    !shouldShowFilteringOptions,
                  )
                }
              >
                <h5 className="text-xs: md:text-sm text-secondary hover:text-primary">
                  Advanced Search{" "}
                  <FontAwesomeIcon
                    icon={
                      shouldShowFilteringOptions
                        ? faChevronUp
                        : faChevronDown
                    }
                    size="sm"
                  />
                </h5>
              </button>

              {shouldShowFilteringOptions ? (
                <PatientFilteringOptions
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  filterBy={filterBy}
                  setFilterBy={setFilterBy}
                  ascending={ascending}
                  setAscending={setAscending}
                  refetch={refetch}
                />
              ) : null}
            </div>

            <PatientSearchBar
              onPatientClick={onPatientClick}
              isForPatientSelectionAction={
                isForPatientSelectionAction
              }
              setSelectedPatientUuid={setSelectedPatientUuid}
              setSelectedPatientName={setSelectedPatientName}
              itemsAsObjects={filteredItems}
              loadingTrigger={loadingTrigger}
            />
          </div>
          <div
            id="newPatient"
            className="flex grow-0 self-end md:self-center"
          >
            <NewPatient
              isForPatientSelectionAction={
                isForPatientSelectionAction
              }
              setSelectedPatientUuid={setSelectedPatientUuid}
              setSelectedPatientName={setSelectedPatientName}
              shouldShow={shouldShow}
              setShouldShow={setShouldShow}
              dataTestId="newPatientBtn"
            />
          </div>
        </div>
      </div>
    );
  }
}

export default Patients;
